import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";
import type { TreatmentRoute } from "./workerOperations";

export interface TreatmentProtocolOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface TreatmentProtocolOperationOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
}

export interface TreatmentProtocolConfig {
  medicineName?: string;
  doseValue?: number;
  doseUnit?: string;
  durationDays?: number;
}

function options(value?: TreatmentProtocolOperationOptions) {
  return {
    ...(value?.clock ? { clock: value.clock } : {}),
    ...(value?.operationIdSource ? { idSource: value.operationIdSource } : {}),
  };
}

function validateContext(context: TreatmentProtocolOperationContext) {
  if (!context.actorId || !context.farmId || !context.deviceId || !context.performedAt) {
    throw new Error("TREATMENT_PROTOCOL_CONTEXT_REQUIRED");
  }
}

function normalizedConfig(config: TreatmentProtocolConfig): Record<string, unknown> {
  const medicineName = config.medicineName?.trim();
  const doseUnit = config.doseUnit?.trim();

  if (
    config.doseValue !== undefined &&
    (!Number.isFinite(config.doseValue) || config.doseValue <= 0)
  ) {
    throw new Error("TREATMENT_PROTOCOL_DOSE_INVALID");
  }

  if (
    config.durationDays !== undefined &&
    (!Number.isInteger(config.durationDays) || config.durationDays < 1 || config.durationDays > 365)
  ) {
    throw new Error("TREATMENT_PROTOCOL_DURATION_INVALID");
  }

  return {
    ...(medicineName ? { medicineName } : {}),
    ...(config.doseValue === undefined ? {} : { doseValue: config.doseValue }),
    ...(doseUnit ? { doseUnit } : {}),
    ...(config.durationDays === undefined ? {} : { durationDays: config.durationDays }),
  };
}

export function buildTreatmentProtocolUpsertOperation(
  context: TreatmentProtocolOperationContext,
  input: {
    protocolId: string;
    expectedVersion: number;
    protocolCode: string;
    displayName: string;
    route: TreatmentRoute;
    requiresIsolation: boolean;
    requiresSumpDisconnect: boolean;
    managerApprovalRequired: boolean;
    protocolConfig: TreatmentProtocolConfig;
    isActive: boolean;
  },
  operationOptions?: TreatmentProtocolOperationOptions,
): OfflineOperation {
  validateContext(context);

  const protocolCode = input.protocolCode.trim().toUpperCase();
  const displayName = input.displayName.trim();

  if (!input.protocolId || protocolCode.length < 1 || protocolCode.length > 40) {
    throw new Error("TREATMENT_PROTOCOL_CODE_INVALID");
  }

  if (displayName.length < 1 || displayName.length > 160) {
    throw new Error("TREATMENT_PROTOCOL_NAME_INVALID");
  }

  if (!Number.isInteger(input.expectedVersion) || input.expectedVersion < 0) {
    throw new Error("TREATMENT_PROTOCOL_VERSION_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "treatment.protocol.upsert",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.protocolId,
      performedAt: context.performedAt,
      baseVersion: String(input.expectedVersion),
      payload: {
        protocolId: input.protocolId,
        expectedVersion: input.expectedVersion,
        protocolCode,
        displayName,
        route: input.route,
        requiresIsolation: input.requiresIsolation,
        requiresSumpDisconnect: input.requiresSumpDisconnect,
        managerApprovalRequired: input.managerApprovalRequired,
        protocolConfig: normalizedConfig(input.protocolConfig),
        isActive: input.isActive,
      },
    },
    options(operationOptions),
  );
}

export function buildGroupBathTreatmentOperation(
  context: TreatmentProtocolOperationContext,
  input: {
    protocolId: string;
    tankIds: string[];
    medicineName: string;
    route: TreatmentRoute;
    doseValue?: number;
    doseUnit?: string;
    durationDays?: number;
    notes?: string;
  },
  operationOptions?: TreatmentProtocolOperationOptions,
): OfflineOperation {
  validateContext(context);

  const tankIds = [...new Set(input.tankIds.filter(Boolean))];
  const medicineName = input.medicineName.trim();
  const doseUnit = input.doseUnit?.trim();
  const notes = input.notes?.trim();

  if (!input.protocolId) {
    throw new Error("TREATMENT_PROTOCOL_REQUIRED");
  }

  if (tankIds.length < 2) {
    throw new Error("TREATMENT_GROUP_BATH_MULTIPLE_TANKS_REQUIRED");
  }

  const entityId = tankIds[0];
  if (!entityId) {
    throw new Error("TREATMENT_GROUP_BATH_MULTIPLE_TANKS_REQUIRED");
  }

  if (!medicineName) {
    throw new Error("TREATMENT_MEDICINE_REQUIRED");
  }

  if (
    input.doseValue !== undefined &&
    (!Number.isFinite(input.doseValue) || input.doseValue <= 0)
  ) {
    throw new Error("TREATMENT_PROTOCOL_DOSE_INVALID");
  }

  if (
    input.durationDays !== undefined &&
    (!Number.isInteger(input.durationDays) || input.durationDays < 1 || input.durationDays > 365)
  ) {
    throw new Error("TREATMENT_PROTOCOL_DURATION_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "hall.treatment",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId,
      performedAt: context.performedAt,
      payload: {
        tankIds,
        ...(notes ? { notes } : {}),
        details: {
          protocolId: input.protocolId,
          medicineName,
          route: input.route,
          ...(input.doseValue === undefined ? {} : { doseValue: input.doseValue }),
          ...(doseUnit ? { doseUnit } : {}),
          ...(input.durationDays === undefined ? {} : { durationDays: input.durationDays }),
          groupBath: true,
        },
      },
    },
    options(operationOptions),
  );
}
